<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import { faArrowLeft, faEye, faFileImport, faFileLines, faFloppyDisk, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import BlockPalette from './block-palette.svelte'
    import DesignCanvas from './design-canvas.svelte'
    import PreviewOverlay from './preview-overlay.svelte'
    import HeaderFooterSettings from './header-footer-settings.svelte'
    import V1ImportPicker from './v1-import-picker.svelte'
    import AiGeneratePicker from './ai-generate-picker.svelte'
    import { getBlockDefinition, getEffectiveBlockWidth } from '../blocks/block-registry'
    import type { PlacedBlock } from '../types/placed-block'
    import { createDefaultHeaderFooter } from '../types/document-header-footer'
    import type { ConvertedTemplate } from './convert-v1-to-v2'
    import { FormTemplateTypeEnum, FormTemplateTypeFriendlyEnum } from '../../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'

    export let formV2TemplateId: string | null = null
    export let initialTitle: string = ''
    export let initialCategory: string = FormTemplateTypeEnum.outro
    export let initialRequiredFields: string[] = []
    export let initialBlocks: PlacedBlock[] = []
    export let initialHeaderFooter = createDefaultHeaderFooter()

    const dispatch = createEventDispatcher()

    // Modelos criados antes do campo "category" existir chegam aqui sem valor — cai no fallback
    // "outro", mesmo tratamento aplicado pelo backend em FormV2TemplatesService.
    const categoryOptions = Object.keys(FormTemplateTypeEnum).map((key) => ({ value: key, label: FormTemplateTypeFriendlyEnum[key] }))

    let title = initialTitle
    let category = initialCategory || FormTemplateTypeEnum.outro
    let requiredFields: string[] = [...(initialRequiredFields ?? [])]
    let blocks: PlacedBlock[] = JSON.parse(JSON.stringify(initialBlocks))
    let headerFooter = JSON.parse(JSON.stringify(initialHeaderFooter))
    let showPreview = false
    let showHeaderFooterSettings = false
    let showV1Picker = false
    let showAiPicker = false
    let saving = false
    let titleInputEl: { focus: () => void }

    function createBlock(blockType: string): PlacedBlock | null {
        const definition = getBlockDefinition(blockType)
        if (!definition) return null
        return {
            id: crypto.randomUUID(),
            type: definition.type,
            label: definition.label,
            data: definition.createDefaultData(),
            required: false,
            readOnlyForPublic: false,
            width: 3,
        }
    }

    function onAdd(event: CustomEvent<{ blockType: string; index?: number }>) {
        const newBlock = createBlock(event.detail.blockType)
        if (!newBlock) return
        const index = event.detail.index ?? blocks.length
        const updated = [...blocks]
        updated.splice(index, 0, newBlock)
        blocks = updated
    }

    function onReorder(event: CustomEvent<{ from: number; to: number }>) {
        const { from, to } = event.detail
        if (from === to) return
        const updated = [...blocks]
        const [moved] = updated.splice(from, 1)
        const insertAt = from < to ? to - 1 : to
        updated.splice(insertAt, 0, moved)
        blocks = updated
    }

    function onDuplicate(event: CustomEvent<{ index: number }>) {
        const source = blocks[event.detail.index]
        if (!source) return
        const clone: PlacedBlock = {
            id: crypto.randomUUID(),
            type: source.type,
            label: source.label,
            data: JSON.parse(JSON.stringify(source.data)),
            required: source.required,
            readOnlyForPublic: source.readOnlyForPublic,
            width: source.width ?? 3,
        }
        const updated = [...blocks]
        updated.splice(event.detail.index + 1, 0, clone)
        blocks = updated
    }

    function onRemove(event: CustomEvent<{ index: number }>) {
        blocks = blocks.filter((_, i) => i !== event.detail.index)
    }

    // width é mutado in-place no objeto do bloco (evita reescrever createBlock/onDuplicate);
    // reatribuir blocks força o grid do canvas a recalcular o agrupamento em linhas.
    function onWidthChange() {
        blocks = blocks
    }

    function extractExtFromDataUrl(dataUrl: string): string {
        const mime = dataUrl.match(/^data:([^;]+);/)?.[1] ?? ''
        const map: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' }
        return map[mime] ?? 'png'
    }

    async function persist() {
        let hf = headerFooter
        if (hf?.header?.logo?.startsWith('data:')) {
            const ext = extractExtFromDataUrl(hf.header.logo)
            const { url } = await window.api.events.files.uploadToSignedLink({ content: hf.header.logo, extension: ext, folderName: 'forms-v2' })
            hf = { ...hf, header: { ...hf.header, logo: url } }
            headerFooter = hf
        }

        const document = {
            headerFooter: hf,
            blocks: blocks.map((block) => ({
                id: block.id,
                type: block.type,
                label: block.label,
                data: block.data,
                required: block.required,
                readOnlyForPublic: block.readOnlyForPublic,
                width: getEffectiveBlockWidth(block),
            })),
        }
        if (formV2TemplateId) {
            await window.api.tools.formsV2.formTemplates.update(formV2TemplateId, { title, category, requiredFields, document })
        } else {
            const result = await window.api.tools.formsV2.formTemplates.create({ title, category, requiredFields, document })
            formV2TemplateId = result.formV2TemplateId
        }
        dispatch('saved', { formV2TemplateId })
    }

    function validateTitle(): boolean {
        if (title.trim()) return true
        window.alert('Informe o título do modelo antes de salvar.')
        titleInputEl?.focus()
        return false
    }

    async function onSave() {
        if (saving || !validateTitle()) return
        saving = true
        try {
            await persist()
        } catch (e) {
            window.error(e)
        } finally {
            saving = false
        }
    }

    function onV1Import(event: CustomEvent<ConvertedTemplate>) {
        const { title: importedTitle, category: importedCategory, blocks: importedBlocks, headerFooter: importedHF } = event.detail
        title = importedTitle
        category = importedCategory || FormTemplateTypeEnum.outro
        blocks = JSON.parse(JSON.stringify(importedBlocks))
        headerFooter = JSON.parse(JSON.stringify(importedHF))
        showV1Picker = false
    }

    // A IA só gera title/category/blocks (ver generate-template-prompt.ts) — headerFooter e
    // requiredFields ficam com o que já estiver no builder, sem serem tocados.
    function onAiImport(event: CustomEvent<{ title: string; category: string; blocks: PlacedBlock[] }>) {
        const { title: generatedTitle, category: generatedCategory, blocks: generatedBlocks } = event.detail
        title = generatedTitle
        category = generatedCategory || FormTemplateTypeEnum.outro
        blocks = JSON.parse(JSON.stringify(generatedBlocks))
        showAiPicker = false
    }

    async function onSaveAndClose() {
        if (saving || !validateTitle()) return
        saving = true
        try {
            await persist()
            dispatch('back')
        } catch (e) {
            window.error(e)
        } finally {
            saving = false
        }
    }

    $: showIA = window.location?.host?.includes('support') || window.location?.host?.includes('localhost') || window.location?.host?.includes('dev')
</script>

<div class="form-builder">
    <Zact>
        <Zbutton color="secondary" icon={faArrowLeft} label="Voltar" on:click={() => dispatch('back')} />
        <div class="category-field">
            <Zinput type="select" label="Categoria" options={categoryOptions} bind:value={category} />
        </div>
        <div class="title-field">
            <Zinput bind:this={titleInputEl} type="text" label="Título do formulário" bind:value={title} required />
        </div>
        <div class="spacer" />
        {#if showIA}
            <Zbutton color="secondary" icon={faWandMagicSparkles} on:click={() => (showAiPicker = true)} />
        {/if}
        <Zbutton color="secondary" icon={faFileImport} label="Importar do v1" on:click={() => (showV1Picker = true)} />
        <Zbutton color="secondary" icon={faFileLines} label="Propriedades" on:click={() => (showHeaderFooterSettings = true)} />
        <Zbutton color="secondary" icon={faEye} label="Visualizar" on:click={() => (showPreview = true)} />
        <Zbutton color="primary" icon={faFloppyDisk} label="Salvar" loading={saving} disabled={saving} on:click={onSave} />
        <Zbutton color="primary" icon={faFloppyDisk} label="Salvar e fechar" loading={saving} disabled={saving} on:click={onSaveAndClose} />
    </Zact>

    <div class="builder-body">
        <BlockPalette on:add={onAdd} />
        <DesignCanvas
            {blocks}
            {headerFooter}
            on:add={onAdd}
            on:reorder={onReorder}
            on:duplicate={onDuplicate}
            on:remove={onRemove}
            on:widthchange={onWidthChange}
        />
    </div>

    {#if showV1Picker}
        <V1ImportPicker on:import={onV1Import} on:close={() => (showV1Picker = false)} />
    {/if}

    {#if showAiPicker}
        <AiGeneratePicker on:import={onAiImport} on:close={() => (showAiPicker = false)} />
    {/if}

    {#if showHeaderFooterSettings}
        <HeaderFooterSettings bind:headerFooter bind:requiredFields on:close={() => (showHeaderFooterSettings = false)} />
    {/if}

    {#if showPreview}
        <PreviewOverlay {title} {blocks} {headerFooter} on:close={() => (showPreview = false)} />
    {/if}
</div>

<style>
    .form-builder {
        --cs2-accent: #4263eb;
        display: flex;
        flex-direction: column;
        height: 100%;
        background: #1a1a1a;
    }
    .category-field {
        flex: 0 0 180px;
    }
    .title-field {
        flex: 1;
        min-width: 160px;
    }
    .builder-body {
        display: flex;
        flex: 1;
        min-height: 0;
    }

    @media (max-width: 900px) {
        .builder-body {
            flex-direction: column;
        }
    }

    /* ───── tema claro ───── */
    :global(.theme_light) .form-builder {
        background: #f4f5f7;
    }
</style>
